<template>
    <div v-if="isIOS" class="VideoPlayer VideoPlayer--native">
        <video
            ref="nativeVideo"
            :src="src"
            :poster="poster"
            :title="title"
            :loop="loop"
            controls
            playsinline
            preload="metadata"
            style="width: 100%; height: 100%; object-fit: cover; border-radius: 8px"
        >
            Ihr Browser unterstützt das Video-Element nicht.
        </video>
    </div>

    <div
        v-else
        class="VideoPlayer"
        :class="{ 'is-playing': isPlaying, 'is-fullscreen': isFullscreen }"
        @mousemove="onMouseMove"
        @mouseleave="onMouseLeave"
    >
        <div class="VideoContainer" ref="videoContainer">
            <video
                ref="video"
                :src="src"
                :poster="poster"
                :title="title"
                :loop="loop"
                preload="metadata"
                playsinline
                @loadedmetadata="onLoadedMetadata"
                @timeupdate="onTimeUpdate"
                @ended="onEnded"
                @play="onPlay"
                @pause="onPause"
                @volumechange="onVolumeChange"
                @click="togglePlay"
                @waiting="onWaiting"
                @canplay="onCanPlay"
            >
                <track kind="captions" />
                Ihr Browser unterstützt das Video-Element nicht.
            </video>

            <!-- Play/Pause Overlay -->
            <div class="PlayOverlay" v-if="!isPlaying && !isLoading" @click="togglePlay">
                <div class="PlayButton">
                    <i class="ri-play-fill"></i>
                </div>
            </div>

            <!-- Loading Overlay -->
            <div class="LoadingOverlay" v-if="isLoading">
                <div class="LoadingSpinner">
                    <i class="ri-loader-4-line"></i>
                </div>
            </div>

            <!-- Controls -->
            <div class="VideoControls" :class="{ 'is-visible': showControls }">
                <!-- Progress Bar -->
                <div class="ProgressContainer">
                    <div
                        class="ProgressBar"
                        ref="progressBar"
                        @click="seekTo"
                        @mousedown="startSeeking"
                        @touchstart="startSeekingTouch"
                    >
                        <div class="ProgressBackground"></div>
                        <div class="ProgressFilled" :style="{ width: progressPercent + '%' }"></div>
                        <div class="ProgressHandle" :style="{ left: progressPercent + '%' }"></div>
                    </div>
                </div>

                <!-- Control Buttons -->
                <div class="ControlsRow">
                    <div class="ControlsLeft">
                        <button
                            class="ControlButton"
                            @click="togglePlay"
                            :title="isPlaying ? 'Pause' : 'Play'"
                        >
                            <i :class="isPlaying ? 'ri-pause-fill' : 'ri-play-fill'"></i>
                        </button>

                        <div class="VolumeContainer">
                            <button
                                class="ControlButton"
                                @click="toggleMute"
                                :title="isMuted ? 'Stummschaltung aufheben' : 'Stummschalten'"
                            >
                                <i :class="volumeIcon"></i>
                            </button>
                            <div
                                class="VolumeSlider"
                                ref="volumeSlider"
                                @click="setVolume"
                                @mousedown="startVolumeChange"
                                @touchstart="startVolumeChangeTouch"
                            >
                                <div class="VolumeBackground"></div>
                                <div
                                    class="VolumeFilled"
                                    :style="{ width: volumePercent + '%' }"
                                ></div>
                                <div
                                    class="VolumeHandle"
                                    :style="{ left: volumePercent + '%' }"
                                ></div>
                            </div>
                        </div>

                        <div class="TimeDisplay">
                            <span>{{ formatTime(currentTime) }}</span>
                            <span>/</span>
                            <span>{{ formatTime(duration) }}</span>
                        </div>
                    </div>

                    <div class="ControlsRight">
                        <button
                            class="ControlButton"
                            @click="toggleFullscreen"
                            :title="isFullscreen ? 'Vollbildmodus beenden' : 'Vollbildmodus'"
                        >
                            <i
                                :class="
                                    isFullscreen ? 'ri-fullscreen-exit-fill' : 'ri-fullscreen-fill'
                                "
                            ></i>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'VideoPlayer',
    props: {
        src: {
            type: String,
            required: true,
        },
        poster: {
            type: String,
            default: '',
        },
        title: {
            type: String,
            default: '',
        },
        autoplay: {
            type: Boolean,
            default: false,
        },
        muted: {
            type: Boolean,
            default: false,
        },
        volume: {
            type: Number,
            default: 1,
            validator: (value) => value >= 0 && value <= 1,
        },
        loop: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            isPlaying: false,
            isLoading: true,
            currentTime: 0,
            duration: 0,
            playerVolume: this.volume,
            isMuted: this.muted,
            isFullscreen: false,
            showControls: true,
            controlsTimeout: null,
            isSeeking: false,
            isVolumeChanging: false,
        };
    },
    computed: {
        isIOS() {
            return /iPad|iPhone|iPod/.test(navigator.userAgent);
        },
        progressPercent() {
            return this.duration > 0 ? (this.currentTime / this.duration) * 100 : 0;
        },
        volumePercent() {
            return this.isMuted ? 0 : this.playerVolume * 100;
        },
        volumeIcon() {
            if (this.isMuted || this.playerVolume === 0) {
                return 'ri-volume-mute-fill';
            } else if (this.playerVolume < 0.5) {
                return 'ri-volume-down-fill';
            } else {
                return 'ri-volume-up-fill';
            }
        },
    },
    watch: {
        src() {
            if (this.isIOS) {
                const videoElement = this.$refs.nativeVideo;
                if (videoElement) {
                    videoElement.load();
                }
            } else if (this.$refs.video) {
                this.isLoading = true;
                this.isPlaying = false;
                this.currentTime = 0;
                this.duration = 0;
                this.$refs.video.load();
                this.$nextTick(() => {
                    this.initializeVideo();
                });
            }
        },
        autoplay(newVal) {
            if (newVal && !this.isIOS) {
                this.$nextTick(() => {
                    this.play();
                });
            } else if (newVal && this.isIOS) {
                this.$nextTick(() => {
                    const videoElement = this.$refs.nativeVideo;
                    if (videoElement) {
                        videoElement.play().catch(() => {});
                    }
                });
            } else if (!newVal) {
                this.pause();
            }
        },
    },
    mounted() {
        if (!this.isIOS) {
            this.setupEventListeners();
            this.initializeVideo();
        }
    },
    beforeUnmount() {
        if (!this.isIOS) {
            this.cleanupEventListeners();
            this.clearControlsTimeout();
        }
    },
    methods: {
        initializeVideo() {
            this.$nextTick(() => {
                if (this.$refs.video) {
                    this.$refs.video.volume = this.playerVolume;
                    this.$refs.video.muted = this.isMuted;
                    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
                    if (this.autoplay && !isIOS) {
                        this.play();
                    }
                }
            });
        },
        setupEventListeners() {
            document.addEventListener('fullscreenchange', this.onFullscreenChange);
            document.addEventListener('webkitfullscreenchange', this.onFullscreenChange);
            document.addEventListener('keydown', this.onKeyDown);
            document.addEventListener('mousemove', this.onGlobalMouseMove);
            document.addEventListener('mouseup', this.onGlobalMouseUp);
            document.addEventListener('touchmove', this.onGlobalMouseMove, { passive: false });
            document.addEventListener('touchend', this.onGlobalMouseUp);
        },
        cleanupEventListeners() {
            document.removeEventListener('fullscreenchange', this.onFullscreenChange);
            document.removeEventListener('webkitfullscreenchange', this.onFullscreenChange);
            document.removeEventListener('keydown', this.onKeyDown);
            document.removeEventListener('mousemove', this.onGlobalMouseMove);
            document.removeEventListener('mouseup', this.onGlobalMouseUp);
            document.removeEventListener('touchmove', this.onGlobalMouseMove);
            document.removeEventListener('touchend', this.onGlobalMouseUp);
        },
        onLoadedMetadata() {
            if (!this.$refs.video) return;
            this.duration = this.$refs.video.duration;
            this.isLoading = false;
        },
        onTimeUpdate() {
            if (!this.$refs.video || this.isSeeking) return;
            this.currentTime = this.$refs.video.currentTime;
        },
        onEnded() {
            this.isPlaying = false;
            this.showControls = true;
            this.clearControlsTimeout();
        },
        onPlay() {
            this.isPlaying = true;
            this.hideControlsAfterDelay();
        },
        onPause() {
            this.isPlaying = false;
            this.showControls = true;
            this.clearControlsTimeout();
        },
        onVolumeChange() {
            if (!this.$refs.video) return;
            this.playerVolume = this.$refs.video.volume;
            this.isMuted = this.$refs.video.muted;
        },
        onWaiting() {
            this.isLoading = true;
        },
        onCanPlay() {
            this.isLoading = false;
        },
        onFullscreenChange() {
            this.isFullscreen = !!document.fullscreenElement;
        },
        onMouseMove() {
            this.showControlsTemporarily();
        },
        onMouseLeave() {
            if (this.isPlaying) {
                this.hideControlsAfterDelay();
            }
        },
        onGlobalMouseMove(event) {
            if (this.isSeeking) {
                this.onProgressMouseMove(event);
            }
            if (this.isVolumeChanging) {
                this.onVolumeMouseMove(event);
            }
        },
        onGlobalMouseUp() {
            this.stopSeeking();
            this.stopVolumeChange();
        },
        onKeyDown(event) {
            if (!this.isFullscreen && !this.$el.contains(event.target)) return;

            switch (event.code) {
                case 'Space':
                    event.preventDefault();
                    this.togglePlay();
                    break;
                case 'ArrowLeft':
                    event.preventDefault();
                    this.seek(-10);
                    break;
                case 'ArrowRight':
                    event.preventDefault();
                    this.seek(10);
                    break;
                case 'ArrowUp':
                    event.preventDefault();
                    this.changeVolume(0.1);
                    break;
                case 'ArrowDown':
                    event.preventDefault();
                    this.changeVolume(-0.1);
                    break;
                case 'KeyM':
                    event.preventDefault();
                    this.toggleMute();
                    break;
                case 'KeyF':
                    event.preventDefault();
                    this.toggleFullscreen();
                    break;
                case 'Escape':
                    if (this.isFullscreen) {
                        event.preventDefault();
                        this.toggleFullscreen();
                    }
                    break;
            }
        },
        togglePlay() {
            if (this.isPlaying) {
                this.pause();
            } else {
                this.play();
            }
        },
        async play() {
            try {
                if (this.isIOS) {
                    const videoElement = this.$el.querySelector('video');
                    if (videoElement) {
                        await videoElement.play();
                    }
                } else {
                    if (!this.$refs.video) return;
                    await this.$refs.video.play();
                }
            } catch (error) {}
        },
        pause() {
            if (this.isIOS) {
                const videoElement = this.$el.querySelector('video');
                if (videoElement) {
                    videoElement.pause();
                }
            } else {
                if (!this.$refs.video) return;
                this.$refs.video.pause();
            }
        },
        seekTo(event) {
            if (this.isSeeking || !this.$refs.video) return;
            const rect = event.currentTarget.getBoundingClientRect();
            const clickX = event.clientX - rect.left;
            const percent = Math.max(0, Math.min(1, clickX / rect.width));
            const newTime = percent * this.duration;
            this.$refs.video.currentTime = newTime;
            this.currentTime = newTime;
        },
        seekToTouch(event) {
            if (this.isSeeking || !this.$refs.video) return;
            const rect = event.currentTarget.getBoundingClientRect();
            const touch = event.touches[0] || event.changedTouches[0];
            const clickX = touch.clientX - rect.left;
            const percent = Math.max(0, Math.min(1, clickX / rect.width));
            const newTime = percent * this.duration;
            this.$refs.video.currentTime = newTime;
            this.currentTime = newTime;
        },
        startSeeking(event) {
            this.isSeeking = true;
            this.seekTo(event);
        },
        startSeekingTouch(event) {
            event.preventDefault();
            this.isSeeking = true;
            this.seekToTouch(event);
        },
        onProgressMouseMove(event) {
            if (!this.isSeeking || !this.$refs.progressBar || !this.$refs.video) return;

            const rect = this.$refs.progressBar.getBoundingClientRect();
            const clientX =
                event.clientX || (event.touches && event.touches[0] ? event.touches[0].clientX : 0);
            const clickX = clientX - rect.left;
            const percent = Math.max(0, Math.min(1, clickX / rect.width));
            const newTime = percent * this.duration;
            this.currentTime = newTime;
            this.$refs.video.currentTime = newTime;
        },
        stopSeeking() {
            this.isSeeking = false;
        },
        seek(seconds) {
            if (!this.$refs.video) return;
            const newTime = Math.max(0, Math.min(this.duration, this.currentTime + seconds));
            this.$refs.video.currentTime = newTime;
            this.currentTime = newTime;
        },
        setVolume(event) {
            if (this.isVolumeChanging || !this.$refs.video) return;
            const rect = event.currentTarget.getBoundingClientRect();
            const clickX = event.clientX - rect.left;
            const percent = Math.max(0, Math.min(1, clickX / rect.width));
            this.playerVolume = percent;
            this.$refs.video.volume = percent;
            this.isMuted = percent === 0;
            this.$refs.video.muted = this.isMuted;
        },
        setVolumeTouch(event) {
            if (this.isVolumeChanging || !this.$refs.video) return;
            const rect = event.currentTarget.getBoundingClientRect();
            const touch = event.touches[0] || event.changedTouches[0];
            const clickX = touch.clientX - rect.left;
            const percent = Math.max(0, Math.min(1, clickX / rect.width));
            this.playerVolume = percent;
            this.$refs.video.volume = percent;
            this.isMuted = percent === 0;
            this.$refs.video.muted = this.isMuted;
        },
        startVolumeChange(event) {
            this.isVolumeChanging = true;
            this.setVolume(event);
        },
        startVolumeChangeTouch(event) {
            event.preventDefault();
            this.isVolumeChanging = true;
            this.setVolumeTouch(event);
        },
        onVolumeMouseMove(event) {
            if (!this.isVolumeChanging || !this.$refs.volumeSlider || !this.$refs.video) return;

            const rect = this.$refs.volumeSlider.getBoundingClientRect();
            const clientX =
                event.clientX || (event.touches && event.touches[0] ? event.touches[0].clientX : 0);
            const clickX = clientX - rect.left;
            const percent = Math.max(0, Math.min(1, clickX / rect.width));
            this.playerVolume = percent;
            this.$refs.video.volume = percent;
            this.isMuted = percent === 0;
            this.$refs.video.muted = this.isMuted;
        },
        stopVolumeChange() {
            this.isVolumeChanging = false;
        },
        changeVolume(delta) {
            if (!this.$refs.video) return;
            const newVolume = Math.max(0, Math.min(1, this.playerVolume + delta));
            this.playerVolume = newVolume;
            this.$refs.video.volume = newVolume;
            this.isMuted = newVolume === 0;
            this.$refs.video.muted = this.isMuted;
        },
        toggleMute() {
            if (!this.$refs.video) return;
            this.isMuted = !this.isMuted;
            this.$refs.video.muted = this.isMuted;
        },
        async toggleFullscreen() {
            try {
                if (this.isFullscreen) {
                    if (document.webkitExitFullscreen) {
                        document.webkitExitFullscreen();
                    } else if (document.exitFullscreen) {
                        await document.exitFullscreen();
                    }
                } else {
                    const element = this.$refs.videoContainer;
                    if (element.webkitRequestFullscreen) {
                        element.webkitRequestFullscreen();
                    } else if (element.requestFullscreen) {
                        await element.requestFullscreen();
                    }
                }
            } catch (error) {
                if (!this.isFullscreen) {
                    this.isFullscreen = true;
                    document.body.style.overflow = 'hidden';
                    this.$el.style.position = 'fixed';
                    this.$el.style.top = '0';
                    this.$el.style.left = '0';
                    this.$el.style.width = '100vw';
                    this.$el.style.height = '100vh';
                    this.$el.style.zIndex = '9999';
                } else {
                    this.isFullscreen = false;
                    document.body.style.overflow = '';
                    this.$el.style.position = '';
                    this.$el.style.top = '';
                    this.$el.style.left = '';
                    this.$el.style.width = '';
                    this.$el.style.height = '';
                    this.$el.style.zIndex = '';
                }
            }
        },
        formatTime(seconds) {
            if (!seconds || isNaN(seconds)) return '0:00';

            const hours = Math.floor(seconds / 3600);
            const minutes = Math.floor((seconds % 3600) / 60);
            const secs = Math.floor(seconds % 60);

            if (hours > 0) {
                return `${hours}:${minutes.toString().padStart(2, '0')}:${secs
                    .toString()
                    .padStart(2, '0')}`;
            } else {
                return `${minutes}:${secs.toString().padStart(2, '0')}`;
            }
        },
        showControlsTemporarily() {
            this.showControls = true;
            this.clearControlsTimeout();
            if (this.isPlaying) {
                this.hideControlsAfterDelay();
            }
        },
        hideControlsAfterDelay() {
            this.clearControlsTimeout();
            this.controlsTimeout = setTimeout(() => {
                if (this.isPlaying) {
                    this.showControls = false;
                }
            }, 3000);
        },
        clearControlsTimeout() {
            if (this.controlsTimeout) {
                clearTimeout(this.controlsTimeout);
                this.controlsTimeout = null;
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.VideoPlayer {
    position: relative;
    width: 100%;
    background-color: #000;
    border-radius: 8px;
    overflow: hidden;
    aspect-ratio: 16 / 9;
    height: 100%;

    &--native {
        background-color: transparent;
    }

    .VideoContainer {
        position: relative;
        width: 100%;
        height: 100%;

        video {
            width: 100%;
            height: 100%;
            object-fit: cover;
            cursor: pointer;
        }
    }

    .PlayOverlay {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: rgba(0, 0, 0, 0.3);
        cursor: pointer;
        transition: opacity 0.3s ease;

        .PlayButton {
            width: 80px;
            height: 80px;
            background-color: rgba(255, 255, 255, 0.9);
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: all 0.3s ease;

            i {
                font-size: 2rem;
                color: #000;
                margin-left: 4px;
            }

            &:hover {
                background-color: rgba(255, 255, 255, 1);
                transform: scale(1.1);
            }
        }
    }

    .LoadingOverlay {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: rgba(0, 0, 0, 0.5);

        .LoadingSpinner {
            i {
                font-size: 2rem;
                color: white;
                animation: spin 1s linear infinite;
            }
        }
    }

    .VideoControls {
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        background: linear-gradient(transparent, rgba(0, 0, 0, 0.8));
        padding: 20px;
        opacity: 0;
        transition: opacity 0.3s ease;
        pointer-events: none;

        &.is-visible {
            opacity: 1;
            pointer-events: all;
        }

        .ProgressContainer {
            margin-bottom: 12px;

            .ProgressBar {
                position: relative;
                height: 4px;
                background-color: rgba(255, 255, 255, 0.3);
                border-radius: 2px;
                cursor: pointer;
                transition: height 0.2s ease;

                &:hover {
                    height: 6px;
                }

                .ProgressBackground {
                    position: absolute;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                    background-color: rgba(255, 255, 255, 0.3);
                    border-radius: 2px;
                }

                .ProgressFilled {
                    position: absolute;
                    top: 0;
                    left: 0;
                    height: 100%;
                    background-color: #1976d2;
                    border-radius: 2px;
                    transition: width 0.1s ease;
                }

                .ProgressHandle {
                    position: absolute;
                    top: 50%;
                    width: 12px;
                    height: 12px;
                    background-color: #1976d2;
                    border-radius: 50%;
                    transform: translate(-50%, -50%);
                    opacity: 0;
                    transition: opacity 0.2s ease;
                }

                &:hover .ProgressHandle {
                    opacity: 1;
                }
            }
        }

        .ControlsRow {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 16px;

            .ControlsLeft {
                display: flex;
                align-items: center;
                gap: 12px;

                .ControlButton {
                    background: none;
                    border: none;
                    color: white;
                    font-size: 1.2rem;
                    cursor: pointer;
                    padding: 8px;
                    border-radius: 4px;
                    transition: all 0.2s ease;

                    &:hover {
                        background-color: rgba(255, 255, 255, 0.1);
                    }

                    &:active {
                        transform: scale(0.95);
                    }
                }

                .VolumeContainer {
                    display: flex;
                    align-items: center;
                    gap: 8px;

                    .VolumeSlider {
                        position: relative;
                        width: 60px;
                        height: 4px;
                        background-color: rgba(255, 255, 255, 0.3);
                        border-radius: 2px;
                        cursor: pointer;
                        transition: width 0.3s ease;

                        &:hover {
                            width: 80px;
                        }

                        .VolumeBackground {
                            position: absolute;
                            top: 0;
                            left: 0;
                            width: 100%;
                            height: 100%;
                            background-color: rgba(255, 255, 255, 0.3);
                            border-radius: 2px;
                        }

                        .VolumeFilled {
                            position: absolute;
                            top: 0;
                            left: 0;
                            height: 100%;
                            background-color: #1976d2;
                            border-radius: 2px;
                            transition: width 0.1s ease;
                        }

                        .VolumeHandle {
                            position: absolute;
                            top: 50%;
                            width: 8px;
                            height: 8px;
                            background-color: #1976d2;
                            border-radius: 50%;
                            transform: translate(-50%, -50%);
                            opacity: 0;
                            transition: opacity 0.2s ease;
                        }

                        &:hover .VolumeHandle {
                            opacity: 1;
                        }
                    }
                }

                .TimeDisplay {
                    color: white;
                    font-size: 0.9rem;
                    font-family: monospace;
                    min-width: 80px;
                    text-align: center;
                }
            }

            .ControlsRight {
                .ControlButton {
                    background: none;
                    border: none;
                    color: white;
                    font-size: 1.2rem;
                    cursor: pointer;
                    padding: 8px;
                    border-radius: 4px;
                    transition: all 0.2s ease;

                    &:hover {
                        background-color: rgba(255, 255, 255, 0.1);
                    }

                    &:active {
                        transform: scale(0.95);
                    }
                }
            }
        }
    }

    &.is-fullscreen {
        .VideoControls {
            padding: 30px;
        }
    }
}

@keyframes spin {
    from {
        transform: rotate(0deg);
    }
    to {
        transform: rotate(360deg);
    }
}

@media (max-width: 768px) {
    .VideoPlayer {
        .VideoControls {
            padding: 16px;

            .ControlsRow {
                .ControlsLeft {
                    gap: 8px;

                    .VolumeContainer {
                        .VolumeSlider {
                            width: 40px;

                            &:hover {
                                width: 50px;
                            }
                        }
                    }

                    .TimeDisplay {
                        min-width: 60px;
                        font-size: 0.8rem;
                    }
                }
            }
        }
    }
}
</style>
